// HealthNutri v5 product page: variant selection, section re-render, gallery thumbnails and native cart adds.
// Everything here enhances a working no-JavaScript form (variant <select> posting to /cart/add).
import { CartLinesUpdateEvent } from '@shopify/events';

const money = (cents) =>
  (cents / 100).toLocaleString(document.documentElement.lang || 'en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' });

function initProduct(root) {
  if (root.dataset.hn5Bound) return;
  root.dataset.hn5Bound = '1';
  const section = root.dataset.section;
  const variants = JSON.parse(root.querySelector('[data-hn5-variants]')?.textContent || '[]');
  const form = root.querySelector('form[data-hn5-form]:not([data-hn5-mini])');
  const idInput = form?.querySelector('[data-hn5-id]');
  const opts = root.querySelector('[data-hn5-opts]');
  let request = 0;

  // Swap the no-JS variant <select> for option buttons.
  if (opts) {
    opts.hidden = false;
    root.querySelector('[data-hn5-fallback]')?.setAttribute('hidden', '');
  }

  const selectedOptions = () =>
    [...root.querySelectorAll('[data-hn5-opt]')].map((fs) => fs.querySelector('input:checked')?.value ?? null);

  const findVariant = (values) => variants.find((v) => v.options.every((o, i) => o === values[i]));

  const syncButtons = (variant) => {
    // For multi-option products, mark values that would lead to a sold-out or missing combination.
    const current = selectedOptions();
    root.querySelectorAll('[data-hn5-opt]').forEach((fs, i) => {
      fs.querySelectorAll('input').forEach((input) => {
        const test = [...current];
        test[i] = input.value;
        const match = findVariant(test);
        input.closest('label').classList.toggle('is-out', !match || !match.available);
      });
    });
  };

  const selectVariant = async (variant, { push = true } = {}) => {
    if (!variant || !idInput) return;
    idInput.value = variant.id;
    if (idInput.tagName === 'SELECT') idInput.value = String(variant.id);
    root.querySelectorAll('[data-hn5-opt]').forEach((fs, i) => {
      const input = [...fs.querySelectorAll('input')].find((el) => el.value === variant.options[i]);
      if (input) input.checked = true;
    });
    syncButtons(variant);
    const url = new URL(location.href);
    url.searchParams.set('variant', variant.id);
    if (push) history.replaceState({ hn5Variant: variant.id }, '', url);
    const ticket = ++request;
    root.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${root.dataset.url}?variant=${variant.id}&section_id=${section}`, { credentials: 'same-origin' });
      if (!res.ok || ticket !== request) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      root.querySelectorAll('[data-hn5-swap]').forEach((el) => {
        const next = doc.querySelector(`[data-hn5-swap="${el.dataset.hn5Swap}"]`);
        if (!next) return;
        if (el.tagName === 'BUTTON') {
          el.innerHTML = next.innerHTML;
          el.disabled = next.disabled;
        } else {
          el.replaceWith(document.importNode(next, true));
        }
      });
      bindGallery();
      updateSubtotal();
    } finally {
      if (ticket === request) root.removeAttribute('aria-busy');
    }
  };

  root.addEventListener('change', (e) => {
    if (e.target.closest('[data-hn5-opt]')) selectVariant(findVariant(selectedOptions()));
    if (e.target.matches('[data-hn5-addon]') || e.target.matches('[data-hn5-qty]')) updateSubtotal();
  });
  idInput?.addEventListener('change', () => selectVariant(variants.find((v) => String(v.id) === idInput.value)));

  root.addEventListener('click', (e) => {
    const pick = e.target.closest('[data-hn5-pick]');
    if (pick) {
      e.preventDefault();
      selectVariant(variants.find((v) => String(v.id) === pick.dataset.hn5Pick));
      form?.closest('.hn5-phero')?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  });

  function bindGallery() {
    root.querySelectorAll('[data-hn5-thumb]').forEach((a) => {
      if (a.dataset.bound) return;
      a.dataset.bound = '1';
      a.addEventListener('click', (e) => {
        e.preventDefault();
        const img = root.querySelector('[data-hn5-main] img');
        if (!img) return;
        img.removeAttribute('srcset');
        img.src = a.dataset.src;
        img.srcset = a.dataset.srcset;
        img.alt = a.dataset.alt;
        root.querySelectorAll('[data-hn5-thumb]').forEach((t) => t.removeAttribute('aria-current'));
        a.setAttribute('aria-current', 'true');
      });
    });
  }
  bindGallery();

  function updateSubtotal() {
    const out = root.querySelector('[data-hn5-subtotal]');
    if (!out) return;
    const current = variants.find((v) => String(v.id) === String(idInput?.value));
    const qty = Math.max(1, parseInt(form.querySelector('[data-hn5-qty]')?.value, 10) || 1);
    const addon = form.querySelector('[data-hn5-addon]');
    const total = (current?.price ?? Number(out.dataset.base)) * qty + (addon?.checked ? Number(addon.dataset.price) : 0);
    out.textContent = `Product subtotal: ${money(total)}`;
  }

  window.addEventListener('popstate', () => {
    const id = new URL(location.href).searchParams.get('variant');
    const v = variants.find((x) => String(x.id) === id);
    if (v) selectVariant(v, { push: false });
  });
}

// Native cart adds for the main form and optional extra cards. Adds the main item first, then any chosen add-on,
// so a failed add-on never blocks or hides the main item; each outcome is reported in plain text.
async function addLines(form, lines) {
  const sections = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
      promise: deferred.promise,
    })
  );
  const results = [];
  let lastSections;
  for (const line of lines) {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(line.id), quantity: line.quantity }], sections: sections.join(',') }),
      credentials: 'same-origin',
    });
    const data = await res.json().catch(() => ({}));
    results.push({ line, ok: res.ok && !data.status, message: data.description || data.message || 'Could not be added.', item: data.items?.[0] });
    if (res.ok && data.sections) lastSections = data.sections;
    if (!results.at(-1).ok && line.main) break;
  }
  const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin' }).then((r) => r.json());
  const anyOk = results.some((r) => r.ok);
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { items: cart.items, source: 'hn5-product', sourceId: 'hn5', itemCount: cart.item_count, sections: lastSections, didError: !anyOk },
  });
  return results;
}

document.addEventListener('submit', async (e) => {
  const form = e.target.closest('form[data-hn5-form]');
  if (!form) return;
  e.preventDefault();
  const status = form.querySelector('[data-hn5-status]');
  const button = form.querySelector('button[type=submit]');
  if (button.dataset.pending) return;
  const fd = new FormData(form);
  const mainId = fd.get('items[0][id]');
  if (!mainId) return;
  const lines = [{ id: mainId, quantity: Math.max(1, parseInt(fd.get('items[0][quantity]'), 10) || 1), main: true, label: 'Your selection' }];
  button.disabled = true;
  button.dataset.pending = '1';
  if (status) status.textContent = 'Adding to cart…';
  try {
    const [main] = await addLines(form, lines);
    if (!main.ok) {
      status.textContent = /^Only /.test(main.message) ? main.message : `Not added: ${main.message}`;
    } else {
      status.textContent = 'Added to cart.';
      showCartOffers(form, main.item);
    }
  } catch {
    if (status) status.textContent = 'Something went wrong. Please try again.';
  } finally {
    button.disabled = false;
    delete button.dataset.pending;
  }
});

// ---------- After-add offers in the native cart drawer ----------
// Shown only after a successful add from a product form. Offers come from the approved explicit mapping rendered by
// the product section ([data-hn5-offers]); products already in the cart are excluded and at most two are shown.
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const fetchCart = () => fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin' }).then((r) => r.json());

async function waitForDrawer(ms = 4000) {
  const start = Date.now();
  while (Date.now() - start < ms) {
    const dlg = document.querySelector('cart-drawer-component')?.closest('dialog');
    if (dlg?.open) return dlg;
    await new Promise((r) => setTimeout(r, 100));
  }
  return document.querySelector('cart-drawer-component')?.closest('dialog') || null;
}

// Only one offer session may exist. A new add (drawer open or reopened, or a quick second click) ends the previous
// session first, so its observer can never re-attach an old panel next to the new one.
let offerSession = null;

// Desktop: the panel sits under the drawer header (approved layout). Phones: after the cart lines, inside the scrolling
// content, so the line's quantity controls stay in view above the sticky Checkout summary.
function placeOffers(box, inner) {
  const summary = inner.querySelector('.cart-drawer__content > .cart-drawer__summary');
  if (summary && matchMedia('(max-width: 749px)').matches) { summary.before(box); return; }
  const header = inner.querySelector('.theme-drawer__header');
  header ? header.after(box) : inner.prepend(box);
}

async function showCartOffers(form, item) {
  offerSession?.end();
  const session = { ended: false, end() { this.ended = true; this.cleanup?.(); } };
  offerSession = session;
  const data = form.closest('[data-hn5-product]')?.querySelector('[data-hn5-offers]');
  const offers = JSON.parse(data?.textContent || '[]');
  const group = data?.dataset.group || '';
  const opener = form.querySelector('button[type=submit]');
  const dlg = await waitForDrawer();
  if (session.ended) return;
  const inner = dlg?.querySelector('.cart-drawer__inner');
  if (!inner) return;
  document.querySelectorAll('.hn5-upsell').forEach((el) => el.remove());
  const box = document.createElement('section');
  box.className = 'hn5-upsell';
  box.setAttribute('aria-label', 'Added to your cart');
  const added = item ? `${esc(item.product_title?.replace(/^BLACKLABEL /, ''))}${item.variant_title ? ` · ${esc(item.variant_title)}` : ''}` : '';
  box.innerHTML = `<p class="hn5-upsell__done" role="status"><span aria-hidden="true">✓</span> <strong>Added to your cart</strong>${added ? `<span>${added}</span>` : ''}</p><div class="hn5-upsell__offers" hidden></div>`;
  placeOffers(box, inner);
  session.cleanup = () => box.remove();
  await renderOffers(box, offers, group);
  if (session.ended) return;
  // Horizon re-renders the drawer contents on every cart change. Re-attach this same block (keeping its state) and keep
  // offer states in step with the cart when lines change or are removed (no reopening, no new offers).
  let t;
  const sync = async () => {
    if (!dlg.open || session.ended) return;
    if (!box.isConnected) {
      const cur = dlg.querySelector('.cart-drawer__inner');
      if (!cur) return;
      placeOffers(box, cur);
    }
    if (box.querySelector('[data-pending]')) return;
    const ids = new Set((await fetchCart()).items.map((i) => i.product_id));
    box.querySelectorAll('[data-up]').forEach((li) => {
      const b = li.querySelector('[data-up-add]');
      if (!ids.has(Number(li.dataset.up)) && b.dataset.done) {
        delete b.dataset.done; b.disabled = false; b.textContent = 'Add';
        li.querySelector('select')?.removeAttribute('disabled'); li.querySelector('.hn5-upsell__msg').textContent = '';
      } else if (ids.has(Number(li.dataset.up)) && !b.dataset.done) {
        li.remove();
      }
    });
    const list = box.querySelector('.hn5-upsell__list');
    if (list && !list.children.length) box.querySelector('.hn5-upsell__offers').hidden = true;
  };
  const mo = new MutationObserver((recs) => {
    if (recs.every((r) => box.contains(r.target))) return;
    clearTimeout(t); t = setTimeout(sync, 250);
  });
  mo.observe(dlg, { childList: true, subtree: true });
  const onClose = () => { session.end(); if (offerSession === session) offerSession = null; opener?.focus({ preventScroll: true }); };
  session.cleanup = () => { mo.disconnect(); clearTimeout(t); dlg.removeEventListener('close', onClose); box.remove(); };
  dlg.addEventListener('close', onClose);
}

// Products included in each stack (snippets/hn5-product-data "components"). Health & Wellness Stack contents are not
// confirmed (description lists four, photo shows five), so it blocks every wellness offer rather than guessing.
const WELLNESS = ['multi-gummy', 'omega-3', 'magnesium-glycinate', 'sensoril®-ashwagandha', 'tamaflex®'];
const STACK_CONTENTS = {
  'performance-stack': ['pure-power', 'pre-hustle-v3', 'beta-alanine'],
  'starter-stack': ['post-hustle', 'creatine-monohydrate'],
  'favorites-stack': ['performance-greens', 'creatine-monohydrate', 'pre-game'],
  'training-stack': ['pure-power', 'pre-hustle-v3', 'post-hustle', 'beta-alanine'],
  'health-wellness-stack': WELLNESS,
};
const decodeHandle = (h) => { try { return decodeURIComponent(h || ''); } catch { return h || ''; } };

async function renderOffers(box, offers, group) {
  const wrap = box.querySelector('.hn5-upsell__offers');
  const cart = await fetchCart();
  const inCart = new Set(cart.items.map((i) => i.product_id));
  const inStack = new Set(cart.items.flatMap((i) => STACK_CONTENTS[decodeHandle(i.handle)] || []));
  const shown = offers.filter((o) => !inCart.has(o.product_id) && !inStack.has(decodeHandle(o.handle)) && o.variants.some((v) => v.available)).slice(0, 2);
  if (!shown.length) { wrap.hidden = true; wrap.innerHTML = ''; return; }
  const money = (c) => (c / 100).toLocaleString(document.documentElement.lang || 'en-US', { style: 'currency', currency: cart.currency || 'USD' });
  const head = group === 'wellness'
    ? '<p class="hn5-upsell__title">Explore more wellness products.</p><p class="hn5-upsell__sub">Optional. Open a product to review its ingredients and warnings.</p>'
    : '<p class="hn5-upsell__title">Add something to go with it?</p><p class="hn5-upsell__sub">These extras are optional.</p>';
  wrap.innerHTML = `${head}<ul class="hn5-upsell__list" role="list">${shown.map((o) => {
    const first = o.variants.find((v) => v.available);
    const sel = o.single ? '' : `<label class="hn5-vh" for="hn5-up-${o.product_id}">${esc(o.option)} for ${esc(o.title)}</label><select id="hn5-up-${o.product_id}" data-up-variant>${o.variants.map((v) => `<option value="${v.id}" data-price="${v.price}" data-img="${esc(v.image || '')}"${v.available ? '' : ' disabled'}${v.id === first.id ? ' selected' : ''}>${esc(v.title)}${v.available ? '' : ' (sold out)'}</option>`).join('')}</select>`;
    return `<li class="hn5-upsell__item" data-up="${o.product_id}"><a class="hn5-upsell__img" href="${esc(o.url)}"><img src="${esc(first.image || o.image)}" alt="" width="64" height="64" loading="lazy"></a><div class="hn5-upsell__body"><a class="hn5-upsell__name" href="${esc(o.url)}">${esc(o.title)}</a><span class="hn5-upsell__reason">${esc(o.reason)}</span><span class="hn5-upsell__price" data-up-price>${money(first.price)}</span>${sel}<p class="hn5-upsell__msg" aria-live="polite"></p></div><button type="button" class="hn5-upsell__add" data-up-add data-variant="${first.id}">Add</button></li>`;
  }).join('')}</ul>`;
  wrap.hidden = false;
  wrap.querySelectorAll('[data-up-variant]').forEach((s) => s.addEventListener('change', () => {
    const li = s.closest('li'); const opt = s.selectedOptions[0];
    li.querySelector('[data-up-add]').dataset.variant = s.value;
    li.querySelector('[data-up-price]').textContent = money(Number(opt.dataset.price));
    if (opt.dataset.img) li.querySelector('img').src = opt.dataset.img;
  }));
  wrap.querySelectorAll('[data-up-add]').forEach((b) => b.addEventListener('click', async () => {
    if (b.dataset.pending || b.dataset.done) return;
    const li = b.closest('li'); const msg = li.querySelector('.hn5-upsell__msg');
    b.dataset.pending = '1'; b.disabled = true; b.textContent = 'Adding…';
    try {
      const [r] = await addLines(box, [{ id: b.dataset.variant, quantity: 1, main: true }]);
      if (r.ok) { b.dataset.done = '1'; b.textContent = 'Added'; msg.textContent = 'Added to your cart.'; li.querySelector('select')?.setAttribute('disabled', ''); }
      else { b.disabled = false; b.textContent = 'Add'; msg.textContent = `Not added: ${r.message}`; }
    } catch { b.disabled = false; b.textContent = 'Add'; msg.textContent = 'Something went wrong. Please try again.'; }
    finally { delete b.dataset.pending; }
  }));
}

document.querySelectorAll('[data-hn5-product]').forEach(initProduct);
